import {useForm} from "react-hook-form";
import {Button} from "../../../basic/button.tsx";
import {Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle} from "../../../basic/dialog.tsx";
import {Input} from "../../../basic/input.tsx";
import {Label} from "../../../basic/label.tsx";
import {useTranslationWithKeys} from "../../../../hooks/useTranslationWithKeys";
import {useState} from "react";
import {toast} from "sonner";
import {useUpdatePortalTeamMutation} from "../../../../store/generated/api";
import {Minus, Plus} from "lucide-react";
import {Separator} from "../../../basic/separator.tsx";
import {DialogType, useDialogContext} from "../../../../contexts/DialogContext";

interface TrialDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    productKey: string;
    defaultQuantity?: number;
    onSubmit: (productKey: string, quantity: number) => void;
}

interface FormData {
    companyName: string;
    phoneNumber: string;
}

export function TrialDialog({open, onOpenChange, productKey, defaultQuantity = 3, onSubmit}: TrialDialogProps) {
    const {t} = useTranslationWithKeys();
    const {register, handleSubmit, reset, formState: {isSubmitting, isValid, errors}} = useForm<FormData>({
        mode: 'onChange'
    });
    const [updatePortalTeam, {isLoading: isUpdatingTeam}] = useUpdatePortalTeamMutation();
    const {closeDialog} = useDialogContext();
    const [quantity, setQuantity] = useState(defaultQuantity);

    const getProductName = (key: string): string => {
        const translationKey = key === 'education' ? 'educational' : key.replace('_', '.');
        return t(`pricing.products.${translationKey}.label`);
    };

    const handleFormSubmit = async (data: FormData) => {
        try {
            await updatePortalTeam({
                vwpTeamUpdateRequest: {
                    name: data.companyName || undefined,
                    phone: data.phoneNumber.trim()
                }
            }).unwrap();
        } catch {
            toast.error(t('error.generic', {message: t('common.error')}));
            return;
        }
        onSubmit(productKey, quantity);
        closeDialog(DialogType.TRIAL);
    };

    const handleClose = () => {
        reset();
        setQuantity(defaultQuantity);
        closeDialog(DialogType.TRIAL);
    };

    return (
        <Dialog open={open} onOpenChange={(open) => !open && closeDialog(DialogType.TRIAL)}>
            <DialogContent className="max-w-md">
                <DialogHeader>
                    <DialogTitle>{t('trial.dialog.title', {productName: getProductName(productKey)})}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4 py-4">
                    <div className="flex items-center justify-between">
                        <Label>{t('trial.dialog.quantity')}</Label>
                        <div className="flex items-center gap-2">
                            <Button type="button" variant="outline" size="icon"
                                    onClick={() => setQuantity(Math.max(1, quantity - 1))}>
                                <Minus className="h-4 w-4"/>
                            </Button>
                            <span className="w-12 text-center">{quantity}</span>
                            <Button type="button" variant="outline" size="icon"
                                    onClick={() => setQuantity(Math.min(10, quantity + 1))}>
                                <Plus className="h-4 w-4"/>
                            </Button>
                        </div>
                    </div>
                    <Separator/>
                    <div className="space-y-4">
                        <h3 className="text-sm font-medium">{t('trial.dialog.subtitle')}</h3>
                        <div className="space-y-2">
                            <Label htmlFor="companyName">{t('trial.dialog.companyName')}</Label>
                            <Input
                                id="companyName"
                                {...register('companyName')}
                                placeholder={t('trial.dialog.companyNamePlaceholder')}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="phoneNumber">{t('trial.dialog.phoneNumber')} *</Label>
                            <Input
                                id="phoneNumber"
                                {...register('phoneNumber', {
                                    required: t('settings.phoneNumberRequired'),
                                    validate: {
                                        notEmpty: (value) => value.trim().length > 0 || t('settings.phoneNumberRequired'),
                                        validChars: (value) => /^[+()\d\s.-]+$/.test(value.trim()) || t('settings.phoneNumberRequired'),
                                        minDigits: (value) => (value.match(/\d/g)?.length ?? 0) >= 6 || t('settings.phoneNumberRequired')
                                    }
                                })}
                                placeholder={t('trial.dialog.phoneNumberPlaceholder')}
                            />
                            {errors.phoneNumber && <p className="text-destructive text-sm">{errors.phoneNumber.message}</p>}
                        </div>
                    </div>
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={handleClose}>
                            {t('common.cancel')}
                        </Button>
                        <Button type="submit" disabled={isSubmitting || isUpdatingTeam || !isValid}>
                            {t('trial.dialog.submit')}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
